import { Auth0Provider, useAuth0 } from "@auth0/auth0-react";
import React, { PropsWithChildren, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CtyButton } from "@circuitry-ai/doc-ui";
import { useTranslation } from "react-i18next";

interface Auth0ProviderWithHookInterceptorProps {
    children: React.ReactNode;
}

/**
 * Set up global fetch and XHR interceptors that trigger the provided 401 handler.
 * This is a plain utility function (outside of any React component) so using `this`
 * with XMLHttpRequest is safe and does not violate React functional component rules.
 */
function setupGlobal401Interceptors(handle401: () => void) {
    // Store the original fetch function
    const originalFetch = window.fetch;
    const originalXHRSend = XMLHttpRequest.prototype.send;

    /**
     * Enhanced fetch function that intercepts all API calls
     * and handles 401 errors globally
     */
    const interceptedFetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
        const response = await originalFetch(input, init);

        if (response.status === 401) {
            handle401();
            // Don't return the 401 response - throw an error instead
            // throw new Error('Unauthorized: 401 error detected and session-expired flow triggered');
        }

        return response;
    };

    /**
     * XMLHttpRequest interceptor to handle 401 errors
     */
    const interceptedXHRSend = function (this: XMLHttpRequest, body?: Document | XMLHttpRequestBodyInit | null) {
        this.addEventListener('readystatechange', () => {
            if (this.readyState === 4 && this.status === 401) {
                handle401();
                // this.dispatchEvent(new ErrorEvent('error', {
                //     message: 'Unauthorized: 401 error detected and session-expired flow triggered'
                // }));
            }
        });

        return originalXHRSend.call(this, body);
    };

    // Replace the global fetch with our intercepted version
    window.fetch = interceptedFetch;

    // Replace XMLHttpRequest send method to intercept XHR calls
    XMLHttpRequest.prototype.send = interceptedXHRSend;

    // Return cleanup to restore originals
    return () => {
        window.fetch = originalFetch;
        XMLHttpRequest.prototype.send = originalXHRSend;
    };
}

// Inner component that has access to Auth0 context
const Auth0InterceptorWrapper = ({ children }: PropsWithChildren) => {
    const { loginWithRedirect } = useAuth0();
    const { t } = useTranslation();

    const [showSessionExpired, setShowSessionExpired] = useState(false);
    const [countdown, setCountdown] = useState(50);
    const isRedirectingRef = useRef(false);
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const redirectToLogin = useCallback(async () => {
        if (isRedirectingRef.current) return;

        isRedirectingRef.current = true;

        // Clear authentication data
        localStorage.removeItem('auth0.is.authenticated');
        sessionStorage.clear();

        // Clear any other auth-related data
        Object.keys(localStorage).forEach(key => {
            if (key.includes('auth0') || key.includes('auth')) {
                localStorage.removeItem(key);
            }
        });

        try {
            await loginWithRedirect({
                appState: { returnTo: globalThis.location.pathname + globalThis.location.search },
                authorizationParams: {
                    // Force showing the Auth0 login screen instead of silently reusing the session
                    prompt: "login",
                },
            });
        } catch (error) {
            console.error('Error during Auth0 login redirect:', error);
            // Fallback to regular redirect if Auth0 fails
            window.location.href = '/';
        }
    }, [loginWithRedirect]);

    const handle401 = useCallback(() => {
        // If modal is already shown, don't restart the flow
        // setShowSessionExpired(prev => {
        //     if (prev) return prev;
        //     return true;
        // });
        // setCountdown(5);
    }, []);

    // Countdown timer for session expired modal
    useEffect(() => {
        if (!showSessionExpired) return;

        if (timerRef.current) {
            globalThis.clearInterval(timerRef.current);
        }

        timerRef.current = globalThis.setInterval(() => {
            setCountdown(prev => {
                if (prev <= 1) {
                    if (timerRef.current) {
                        globalThis.clearInterval(timerRef.current);
                        timerRef.current = null;
                    }
                    redirectToLogin().catch((error) => {
                        console.error('Error during redirectToLogin from countdown:', error);
                    });
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => {
            if (timerRef.current) {
                globalThis.clearInterval(timerRef.current);
                timerRef.current = null;
            }
        };
    }, [showSessionExpired, redirectToLogin]);

    useEffect(() => {
        const cleanup = setupGlobal401Interceptors(handle401);
        return cleanup;
    }, [handle401]);

    return (
        <>
            <div className={showSessionExpired ? "pointer-events-none blur-sm" : ""}>
                {children}
            </div>
            {showSessionExpired && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
                    <div className="relative z-10 max-w-md w-full mx-4">
                        <div className="bg-background/90 backdrop-blur-md border border-default-200 shadow-2xl rounded-2xl p-6 md:p-7">
                            <div className="flex items-center mb-4">
                                <div className="mr-3 flex h-10 w-10 items-center justify-center rounded-full bg-danger/10 text-danger text-lg font-semibold">
                                    !
                                </div>
                                <h2 className="text-lg font-semibold text-foreground">
                                    {t("Session expired")}
                                </h2>
                            </div>
                            <p className="text-sm text-default-700 mb-3">
                                {t("Your session has expired. You need to log in again to continue.")}
                            </p>
                            <p className="text-sm text-default-500 mb-6">
                                {t("You will be redirected to the login page in {{seconds}} seconds", { seconds: countdown })}
                            </p>
                            <div className="flex justify-end">
                                <CtyButton
                                    color="primary"
                                    size="sm"
                                    onPress={() => {
                                        if (timerRef.current) {
                                            globalThis.clearInterval(timerRef.current);
                                            timerRef.current = null;
                                        }
                                    redirectToLogin().catch((error) => {
                                        console.error('Error during redirectToLogin from button:', error);
                                    });
                                    }}
                                >
                                    {t("Re-login")}
                                </CtyButton>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export const Auth0ProviderWithHookInterceptor = ({
    children,
}: PropsWithChildren<Auth0ProviderWithHookInterceptorProps>): JSX.Element | null => {
    const domain = import.meta.env.CTY_APP_DOMAIN;
    const clientId = import.meta.env.CTY_APP_CLIENT_ID;
    const redirectUri = window.location.origin;
    const navigate = useNavigate();

    const onRedirectCallback = (appState: { returnTo?: string } = {}) => {
        navigate(appState?.returnTo || window.location.pathname, { replace: true });
    };

    return (
        <Auth0Provider
            domain={domain}
            clientId={clientId}
            authorizationParams={{
                redirect_uri: redirectUri
            }}
            onRedirectCallback={onRedirectCallback}
        >
            <Auth0InterceptorWrapper>
                {children}
            </Auth0InterceptorWrapper>
        </Auth0Provider>
    );
};
